import type {
  AgentOption,
  ClientOption,
  FieldEngineerRecommendation,
  ParentTicketFormValues,
  RequestOption,
} from '@/types/ticket'
import type {
  ParentTicketStatus,
  SeverityLabel,
  TicketUpdate,
  TicketUpdateFormValues,
} from '@/types/parentTicket'
import { useParentTicketStore } from '@/store/parentTicket'
import { apiClient } from '@/lib/api/client'

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
  errors?: Record<string, string[]>
}

const MOCK_REQUESTS: RequestOption[] = [
  {
    id: 'R000001',
    clientName: 'Alberto Ripley',
    clientOrg: 'SM Investment Inc',
    serialNumber: 'SN-24A7-91KF',
    unitModel: 'Dell Latitude 5420',
    requestedAt: new Date('2026-01-10T10:00:49+08:00').toISOString(),
    issueDescription: 'Laptop keeps freezing when opening POS software. Happens after 10 minutes of use.',
  },
  {
    id: 'R000002',
    clientName: 'Karen Dela Cruz',
    clientOrg: 'JFC Store Support',
    serialNumber: 'SN-88XY-1234',
    unitModel: 'Epson TM-T88VI',
    requestedAt: new Date('2026-01-10T11:22:10+08:00').toISOString(),
    issueDescription: 'Printer paper jam error',
  },
  {
    id: 'R000003',
    clientName: 'Carol Lam',
    clientOrg: 'JG Summit Holdings',
    serialNumber: 'SN-55BZ-4021',
    unitModel: 'HP LaserJet Pro M404dn',
    requestedAt: new Date('2026-01-11T08:15:00+08:00').toISOString(),
    issueDescription: 'Print queue stuck; nothing comes out even after restart.',
  },
  {
    id: 'R000004',
    clientName: 'Marsha Noland',
    clientOrg: 'Aboitiz Equity Ventures',
    serialNumber: 'SN-HPD-2332',
    unitModel: 'HP DeskJet 2332',
    requestedAt: new Date('2026-01-11T09:40:00+08:00').toISOString(),
    issueDescription: 'Error light blinking on DeskJet 2332; needs troubleshooting.',
  },
  {
    id: 'R000005',
    clientName: 'Janelle Santos',
    clientOrg: 'Robinsons Retail Holdings',
    serialNumber: 'SN-4R11-PM45',
    unitModel: 'Lenovo ThinkPad E14',
    requestedAt: new Date('2026-01-11T10:05:00+08:00').toISOString(),
    issueDescription: 'Office 365 install and activation needed for new user (Teams + Outlook config).',
  },
  {
    id: 'R000006',
    clientName: 'Miguel Tan',
    clientOrg: 'San Miguel Corporation',
    serialNumber: 'SN-D7080-PM42',
    unitModel: 'Dell OptiPlex 7080',
    requestedAt: new Date('2026-01-11T11:00:00+08:00').toISOString(),
    issueDescription: 'Quarterly workstation preventive maintenance for 12 units; preferred 2-4 PM window.',
  },
  {
    id: 'R000007',
    clientName: 'Aira Cruz',
    clientOrg: 'BDO Unibank',
    serialNumber: 'SN-VPN-5521',
    unitModel: 'HP ProBook 430 G8',
    requestedAt: new Date('2026-01-11T11:35:00+08:00').toISOString(),
    issueDescription: 'VPN disconnects every 5-10 minutes; Outlook and Teams drop during calls.',
  },
  {
    id: 'R000008',
    clientName: 'Paolo Reyes',
    clientOrg: 'Globe Telecom',
    serialNumber: 'SN-REL-8808',
    unitModel: 'Dell Latitude 5430',
    requestedAt: new Date('2026-01-11T12:10:00+08:00').toISOString(),
    issueDescription: 'Need onsite reliever for front desk IT support (8 AM-5 PM) due to technician on leave.',
  },
]

const MOCK_CLIENTS: ClientOption[] = [
  {
    storeCode: 'JFC-0123',
    storeName: 'Jollibee - SM North',
    ownershipType: 'Franchise',
    storeEmail: 'store0123@client.com',
    storeContact: '+63 912 345 6789',
    alternateContact: '+63 917 000 1111',
    amName: 'Alberto Ripley',
    amEmail: 'alberto@company.com.ph',
    storeHours: '10:00 AM - 10:00 PM',
    
  },
  {
    storeCode: 'JFC-0456',
    storeName: 'Jollibee - Trinoma',
    ownershipType: 'Company Owned',
    storeEmail: 'store0456@client.com',
    storeContact: '+63 920 111 2222',
    alternateContact: '+63 930 333 4444',
    amName: 'Karen Dela Cruz',
    amEmail: 'karen@company.com.ph',
    storeHours: '9:00 AM - 11:00 PM',
  },
]

export const TicketService = {
  async getRequestOptions(): Promise<ApiResponse<RequestOption[]>> {
    return { success: true, data: MOCK_REQUESTS }
  },

  async searchRequestOptions(searchTerm: string): Promise<ApiResponse<RequestOption[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const response = await apiClient.get('/support/my-request', {
        params: {
          search: searchTerm,
          incoming: 1, // Filter for submitted requests (submitted_at IS NOT NULL)
          acknowledged: 1, // Filter for acknowledged requests (acknowledged_at IS NOT NULL)
          per_page: 20,
        },
      })

      const requestsData = response.data?.data || []
      
      // Transform API response to RequestOption format
      const transformedRequests: RequestOption[] = requestsData.map((req: any) => {
        // Handle categories - API returns single category name, but category_ids is an array
        // For now, use the single category name if available
        const categories: string[] = req.category ? [req.category] : []

        return {
          id: req.request_number || req.id?.toString() || '',
          clientName: req.contact_name || '',
          clientOrg: req.client_name || req.client?.name || undefined,
          serialNumber: req.serial_number || undefined,
          unitModel: req.unit_model || undefined,
          requestedAt: req.requested_at || req.created_at || new Date().toISOString(),
          issueDescription: req.description || undefined,
          categories: categories.length > 0 ? categories : undefined,
          description: req.description || undefined,
          requestedQuantity: req.requested_quantity || undefined,
          preferredScheduleAt: req.preferred_schedule_at || undefined,
          attachmentMetadata: req.attachment_metadata || undefined,
          contactNumber: req.contact_number || undefined,
          contactName: req.contact_name || undefined,
          contactEmail: req.contact_email || undefined,
          preferredContactAt: req.preferred_contact_at || undefined,
          // Service Type
          serviceTypeId: req.service_type_id || undefined,
          serviceType: req.service_type || undefined,
          serviceParent: req.service_parent || undefined,
          // Status & Priority
          ticketStatus: req.ticket_status !== undefined ? req.ticket_status : undefined,
          ticketStatusLabel: req.ticket_status_label || undefined,
          priority: req.priority || undefined,
          // Source & Channel
          requestSource: req.request_source || undefined,
          requestChannel: req.request_channel || undefined,
          // Timeline
          submittedAt: req.submitted_at || undefined,
          acknowledgedAt: req.acknowledged_at || undefined,
          acknowledgedBy: req.acknowledged_by || undefined,
          resolvedAt: req.resolved_at || undefined,
          closedAt: req.closed_at || undefined,
          createdAt: req.created_at || undefined,
          updatedAt: req.updated_at || undefined,
          // Assignment
          assignedTo: req.assigned_to || undefined,
          assignedTeamId: req.assigned_team_id || undefined,
          // External References
          externalReferenceId: req.external_reference_id || undefined,
          externalThreadId: req.external_thread_id || undefined,
          // User/Requestor
          userId: req.user_id || undefined,
          user: req.user || undefined,
          // Ticket ID
          ticketId: req.ticket_id || undefined,
          // Client ID (to determine which database to search stores from)
          clientId: req.client_id || undefined,
          // Client Code (to set X-Client-Code header for store search)
          clientCode: req.client_code || undefined,
        }
      })

      return { success: true, data: transformedRequests }
    } catch (error: any) {
      console.error('Error searching request options:', error)
      return { 
        success: false, 
        message: error?.response?.data?.message || error?.message || 'Failed to search requests',
        data: [] 
      }
    }
  },

  async getClientOptions(): Promise<ApiResponse<ClientOption[]>> {
    return { success: true, data: MOCK_CLIENTS }
  },

  async searchHelpdeskAgentOptions(searchTerm: string): Promise<ApiResponse<AgentOption[]>> {
    try {
      if (searchTerm.length < 2) {
        return { success: true, data: [] }
      }
      const response = await apiClient.get('/options/helpdesk-users/search', {
        params: { search: searchTerm },
      })
      const data = response.data?.data ?? response.data ?? []
      const list = Array.isArray(data) ? data : []
      const agents: AgentOption[] = list.map((u: any) => ({
        id: u.id,
        user_login: u.user_login,
        user_email: u.user_email,
        first_name: u.first_name,
        last_name: u.last_name,
        employee_id: u.employee_id,
      }))
      return { success: true, data: agents }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to search agents',
        data: [],
      }
    }
  },

  async recommendFieldEngineers(storeId: number, clientCode?: string): Promise<ApiResponse<FieldEngineerRecommendation[]>> {
    try {
      const headers: Record<string, string> = {}
      if (clientCode) headers['X-Client-Code'] = clientCode
      const response = await apiClient.get(`/organization/field-engineers/recommend/${storeId}`, {
        params: { limit: 10 },
        headers,
      })
      const recommendationsData = response.data?.data?.recommendations || []
      const recommendations: FieldEngineerRecommendation[] = recommendationsData.map((rec: any) => ({
        field_engineer_id: rec.field_engineer_id,
        user_id: rec.user_id,
        full_name: rec.full_name,
        cluster: rec.cluster,
        distance_km: rec.distance_km,
        score: rec.score,
        match_reasons: rec.match_reasons || [],
        certifications: rec.certifications || [],
        has_required_certifications: rec.has_required_certifications,
        workload: rec.workload,
      }))
      return { success: true, data: recommendations }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to get field engineer recommendations',
        data: [],
      }
    }
  },

  async searchAssets(searchTerm: string, clientCode?: string, storeId?: number): Promise<ApiResponse<any[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const headers: Record<string, string> = {}
      
      // If clientCode is provided, set X-Client-Code header to search assets from that client's database
      if (clientCode) {
        headers['X-Client-Code'] = clientCode
      }

      const params: Record<string, any> = { search: searchTerm }
      
      // If storeId is provided, add it to params to filter assets by store
      if (storeId) {
        params.store_id = storeId
      }

      const response = await apiClient.get('/options/assets/search', {
        params,
        headers,
      })
      const data = response.data ?? []
      const assets = Array.isArray(data) ? data : []
      return { success: true, data: assets }
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to search assets',
        data: [],
      }
    }
  },

  async searchStoreOptions(searchTerm: string, clientCode?: string): Promise<ApiResponse<ClientOption[]>> {
    try {
      if (searchTerm.length < 3) {
        return { success: true, data: [] }
      }

      const headers: Record<string, string> = {}
      
      // If clientCode is provided, set X-Client-Code header to search stores from that client's database
      if (clientCode) {
        headers['X-Client-Code'] = clientCode
      }

      const response = await apiClient.get('/options/stores/search', {
        params: {
          search: searchTerm,
        },
        headers,
      })

      const storesData = response.data?.data || response.data || []
      
      // Transform API response to ClientOption format
      const transformedStores: ClientOption[] = storesData.map((store: any) => ({
        storeCode: store.branch_code || '',
        storeName: store.branch_name || '',
        ownershipType: store.business_unit_name || undefined,
        storeEmail: store.branch_email || undefined,
        storeContact: store.contact_number || store.mobile_number || undefined,
        alternateContact: store.mobile_number || undefined,
        amName: store.contact_person || undefined,
        amEmail: undefined, // Not available in StoreSearchResource
        storeHours: undefined, // Not available in StoreSearchResource
        // Additional fields for display
        branchAddress: store.branch_address || undefined,
        businessUnitName: store.business_unit_name || undefined,
        storeId: store.id || undefined,
        // Location fields
        locationAddress: store.location_address || undefined,
        normalizedAddress: store.normalized_address || undefined,
        latitude: store.latitude !== null && store.latitude !== undefined ? Number(store.latitude) : undefined,
        longitude: store.longitude !== null && store.longitude !== undefined ? Number(store.longitude) : undefined,
      }))

      return { success: true, data: transformedStores }
    } catch (error: any) {
      console.error('Error searching store options:', error)
      return { 
        success: false, 
        message: error?.response?.data?.message || error?.message || 'Failed to search stores',
        data: [] 
      }
    }
  },

  /**
   * Coerce API value to engineer id (number | null | undefined). Handles {} or non-numeric from API.
   */
  _toEngineerId(v: unknown): number | null | undefined {
    if (v == null) return v as null | undefined
    if (typeof v === 'number' && Number.isFinite(v)) return v
    return undefined
  },

  /**
   * Map API parent ticket row (from list or show) to store shape.
   * Used by getParentTicket and by list page to sync DataTable data to store.
   * Includes extra fields for edit form: subject, agentId, scheduledAt, notes, workPermit, assets, source, engineers.
   */
  mapApiRowToParentTicket(
    row: Record<string, unknown>,
    fallbackId?: string
  ): import('@/types/parentTicket').ParentTicket & {
    ticket_number?: string
    store_id?: number
    store_code?: string
    subject?: string
    agentId?: string
    primaryEngineerId?: number | null
    backupEngineerId?: number | null
    scheduledAt?: string
    notes?: string
    workPermit?: string
    work_permit_path?: string
    workPermitPath?: string
    assets?: unknown[]
    source?: string
    type?: string
  } {
    let assets: unknown[] | undefined
    const rawAssets = row.assets
    if (Array.isArray(rawAssets)) {
      assets = rawAssets
    } else if (typeof rawAssets === 'string') {
      try {
        const parsed = JSON.parse(rawAssets) as unknown
        assets = Array.isArray(parsed) ? parsed : undefined
      } catch {
        assets = undefined
      }
    }
    const scheduled = (row.scheduledAt ?? row.scheduled_at) as string | undefined
    const workPermit = (row.workPermit ?? row.work_permit) as string | undefined
    const workPermitPath = (row.work_permit_path ?? row.workPermitPath) as string | undefined
    const ticketNumber = (row.ticket_number ?? row.ticketId) as string | undefined
    return {
      id: String(row.id ?? fallbackId ?? ''),
      ticket_number: ticketNumber ?? String(row.id ?? fallbackId ?? ''),
      requestId: String(row.requestId ?? row.ticket_request_id ?? ''),
      clientName: (row.client as string) ?? (row.clientName as string) ?? '',
      clientOrg: (row.clientOrg as string) ?? '',
      agentName: (row.agent_name as string) ?? null,
      requestedAt: (row.submitted_at as string) ?? (row.created_at as string) ?? '',
      ageingLabel: (row.ageing as string) ?? '',
      severity: ((row.priority as string) === 'Mid' ? 'Medium' : (row.priority as string) || 'Low') as SeverityLabel,
      slaDueAt: (row.dueByAt as string) ?? (row.due_by_at as string) ?? null,
      status: ((row.status as string) ?? 'Open') as ParentTicketStatus,
      description: (row.issueDescription as string) ?? (row.description as string) ?? '',
      store_id: row.store_id as number | undefined,
      store_code: (row.store_code ?? row.storeCode) as string | undefined,
      subject: (row.subject as string) ?? undefined,
      agentId: row.agentId != null ? String(row.agentId) : undefined,
      primaryEngineerId: TicketService._toEngineerId(row.primary_engineer_id ?? row.primaryEngineerId),
      backupEngineerId: TicketService._toEngineerId(row.backup_engineer_id ?? row.backupEngineerId),
      scheduledAt: scheduled ?? undefined,
      notes: (row.notes as string) ?? undefined,
      workPermit: workPermit ?? undefined,
      work_permit_path: workPermitPath ?? undefined,
      workPermitPath: workPermitPath ?? undefined,
      assets: assets ?? undefined,
      source: (row.source as string) ?? undefined,
      type: (row.type as string) ?? undefined,
    }
  },

  /**
   * Fetch a single parent ticket by id (GET /tickets/:id).
   * Use when editing a ticket that may not be in the list store (e.g. direct link or fresh load).
   */
  async getParentTicket(id: string): Promise<ApiResponse<import('@/types/parentTicket').ParentTicket & { store_id?: number; store_code?: string; request?: import('@/types/ticket').RequestOption }>> {
    try {
      const { data } = await apiClient.get<{ data?: Record<string, unknown> }>(`/tickets/${id}`)
      const row = data?.data ?? data
      if (!row || typeof row !== 'object') {
        return { success: false, message: 'Ticket not found' }
      }
      const mapped = TicketService.mapApiRowToParentTicket(row as Record<string, unknown>, id)
      const apiRequest = (row as Record<string, unknown>).request
      const request = apiRequest && typeof apiRequest === 'object' && !Array.isArray(apiRequest)
        ? (apiRequest as import('@/types/ticket').RequestOption)
        : undefined
      const apiStore = (row as Record<string, unknown>).store
      const store = apiStore && typeof apiStore === 'object' && !Array.isArray(apiStore) && 'storeCode' in apiStore
        ? (apiStore as import('@/types/ticket').ClientOption)
        : undefined
      const apiAgent = (row as Record<string, unknown>).agent
      const agent = apiAgent && typeof apiAgent === 'object' && !Array.isArray(apiAgent) && 'id' in apiAgent
        ? (apiAgent as import('@/types/ticket').AgentOption)
        : undefined
      const apiPrimary = (row as Record<string, unknown>).primaryEngineer
      const primaryEngineer = apiPrimary && typeof apiPrimary === 'object' && !Array.isArray(apiPrimary) && 'id' in apiPrimary
        ? (apiPrimary as import('@/store/parentTicket').FieldEngineerSummary)
        : undefined
      const apiBackup = (row as Record<string, unknown>).backupEngineer
      const backupEngineer = apiBackup && typeof apiBackup === 'object' && !Array.isArray(apiBackup) && 'id' in apiBackup
        ? (apiBackup as import('@/store/parentTicket').FieldEngineerSummary)
        : undefined
      return {
        success: true,
        data: {
          ...mapped,
          ...(request && { request }),
          ...(store && { store }),
          ...(agent && { agent }),
          ...(primaryEngineer && { primaryEngineer }),
          ...(backupEngineer && { backupEngineer }),
        },
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to load ticket'
      return { success: false, message }
    }
  },

  async createParentTicket(
    payload: ParentTicketFormValues
  ): Promise<ApiResponse<{ ticketId: string }>> {
    const hasFile = payload.workPermitFile && typeof payload.workPermitFile === 'object' && payload.workPermitFile instanceof File
    const body = hasFile ? new FormData() : ({} as Record<string, unknown>)

    const set = (key: string, value: unknown) => {
      if (hasFile && body instanceof FormData) {
        if (value != null && value !== '') body.append(key, String(value))
      } else if (!hasFile && typeof body === 'object') {
        if (value != null && value !== '') (body as Record<string, unknown>)[key] = value
      }
    }
    const setRequired = (key: string, value: unknown) => {
      const s = value != null ? String(value) : ''
      if (hasFile && body instanceof FormData) {
        body.append(key, s)
      } else if (!hasFile && typeof body === 'object') {
        (body as Record<string, unknown>)[key] = s
      }
    }

    set('requestId', payload.requestId)
    if (payload.storeId != null && payload.storeId !== '') set('storeId', payload.storeId)
    set('storeCode', payload.storeCode ?? '')
    setRequired('subject', payload.subject)
    setRequired('priority', payload.priority)
    setRequired('source', payload.source)
    setRequired('type', payload.type)
    setRequired('issueDescription', payload.issueDescription)
    set('agentId', payload.agentId ?? '')
    if (payload.primaryEngineerId != null) set('primaryEngineerId', payload.primaryEngineerId)
    if (payload.backupEngineerId != null) set('backupEngineerId', payload.backupEngineerId)
    if (payload.scheduledAt) set('scheduledAt', payload.scheduledAt)
    if (payload.notes) set('notes', payload.notes)
    if (payload.workPermit) set('workPermit', payload.workPermit)
    if (payload.submitTicket) set('submitTicket', '1')
    // Always send assets array (even if empty) so backend can update it
    if (payload.assets !== undefined) {
      set('assets', Array.isArray(payload.assets) && payload.assets.length > 0 ? JSON.stringify(payload.assets) : '[]')
    }
    if (hasFile && payload.workPermitFile instanceof File) {
      (body as FormData).append('workPermitFile', payload.workPermitFile)
    }

    try {
      // Do not set Content-Type for FormData so axios sets multipart/form-data with boundary
      const res = await apiClient.post<{ success: boolean; message?: string; data?: { ticketId: string; id?: number }; errors?: Record<string, string[]> }>(
        '/tickets',
        hasFile ? body : (body as Record<string, unknown>)
      )
      const data = res.data
      if (data.success && data.data?.ticketId) {
        return { success: true, message: data.message ?? 'Parent ticket created', data: { ticketId: data.data.ticketId } }
      }
      if (data.errors) {
        return { success: false, message: data.message ?? 'Validation failed', errors: data.errors }
      }
      return { success: true, message: data.message ?? 'Parent ticket created', data: { ticketId: (data.data as { ticketId?: string })?.ticketId ?? '' } }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; message?: string }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to create ticket'
      const errors = ax.response?.data?.errors
      return { success: false, message, ...(errors && { errors }) }
    }
  },

  async updateParentTicket(
    id: string,
    payload: ParentTicketFormValues
  ): Promise<ApiResponse<unknown>> {
    const hasFile = payload.workPermitFile && typeof payload.workPermitFile === 'object' && payload.workPermitFile instanceof File
    const body = hasFile ? new FormData() : ({} as Record<string, unknown>)

    const set = (key: string, value: unknown) => {
      if (hasFile && body instanceof FormData) {
        if (value != null && value !== '') body.append(key, String(value))
      } else if (!hasFile && typeof body === 'object') {
        if (value != null && value !== '') (body as Record<string, unknown>)[key] = value
      }
    }
    const setRequired = (key: string, value: unknown) => {
      const s = value != null ? String(value) : ''
      if (hasFile && body instanceof FormData) {
        body.append(key, s)
      } else if (!hasFile && typeof body === 'object') {
        (body as Record<string, unknown>)[key] = s
      }
    }

    set('_method', 'PUT')
    set('requestId', payload.requestId)
    if (payload.storeId != null && payload.storeId !== '') set('storeId', payload.storeId)
    set('storeCode', payload.storeCode ?? '')
    setRequired('subject', payload.subject)
    setRequired('priority', payload.priority)
    setRequired('source', payload.source)
    setRequired('type', payload.type)
    setRequired('issueDescription', payload.issueDescription)
    set('agentId', payload.agentId ?? '')
    if (payload.primaryEngineerId != null) set('primaryEngineerId', payload.primaryEngineerId)
    if (payload.backupEngineerId != null) set('backupEngineerId', payload.backupEngineerId)
    if (payload.scheduledAt) set('scheduledAt', payload.scheduledAt)
    if (payload.notes) set('notes', payload.notes)
    if (payload.workPermit) set('workPermit', payload.workPermit)
    if (payload.submitTicket) set('submitTicket', '1')
    // Always send assets array (even if empty) so backend can update it
    if (payload.assets !== undefined) {
      set('assets', Array.isArray(payload.assets) && payload.assets.length > 0 ? JSON.stringify(payload.assets) : '[]')
    }
    if (hasFile && payload.workPermitFile instanceof File) {
      (body as FormData).append('workPermitFile', payload.workPermitFile)
    }
    // Never send workPermitFile in JSON (only in FormData when it's a real File) so backend isn't sent {} or a path string
    if (!hasFile && typeof body === 'object' && body !== null && 'workPermitFile' in body) {
      delete (body as Record<string, unknown>)['workPermitFile']
    }

    try {
      // PHP only populates $_FILES for POST; use POST + _method=PUT when uploading file so work_permit_path is saved
      const res = hasFile
        ? await apiClient.post<{ success: boolean; message?: string; data?: unknown; errors?: Record<string, string[]> }>(
            `/tickets/${id}`,
            body as FormData
          )
        : await apiClient.put<{ success: boolean; message?: string; data?: unknown; errors?: Record<string, string[]> }>(
            `/tickets/${id}`,
            body as Record<string, unknown>
          )
      const data = res.data
      if (data.success) {
        useParentTicketStore.getState().updateTicket(id, {
          description: payload.issueDescription ?? undefined,
          severity: payload.priority as import('@/types/parentTicket').SeverityLabel,
        })
        return { success: true, message: data.message ?? 'Parent ticket updated' }
      }
      if (data.errors) return { success: false, message: data.message ?? 'Validation failed', errors: data.errors }
      return { success: true, message: data.message ?? 'Parent ticket updated' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; message?: string }
      const message = ax.response?.data?.message ?? ax.message ?? 'Failed to update ticket'
      const errors = ax.response?.data?.errors
      return { success: false, message, ...(errors && { errors }) }
    }
  },

  // ──────────── Ticket Updates (comments / progress notes) ────────────

  async getTicketUpdates(
    ticketId: string,
    page = 1,
    perPage = 20
  ): Promise<ApiResponse<{ data: TicketUpdate[]; meta?: Record<string, unknown> }>> {
    try {
      const res = await apiClient.get(`/tickets/${ticketId}/updates`, {
        params: { page, per_page: perPage },
      })
      return { success: true, data: res.data }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to load updates' }
    }
  },

  async createTicketUpdate(
    ticketId: string,
    payload: TicketUpdateFormValues
  ): Promise<ApiResponse<TicketUpdate>> {
    try {
      const res = await apiClient.post<{ success: boolean; message?: string; data?: TicketUpdate }>(
        `/tickets/${ticketId}/updates`,
        payload
      )
      if (res.data.success) {
        return { success: true, message: res.data.message ?? 'Update added', data: res.data.data }
      }
      return { success: false, message: res.data.message ?? 'Failed to add update' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to add update' }
    }
  },

  async editTicketUpdate(
    ticketId: string,
    updateId: number,
    payload: TicketUpdateFormValues
  ): Promise<ApiResponse<TicketUpdate>> {
    try {
      const res = await apiClient.put<{ success: boolean; message?: string; data?: TicketUpdate }>(
        `/tickets/${ticketId}/updates/${updateId}`,
        payload
      )
      if (res.data.success) {
        return { success: true, message: res.data.message ?? 'Update modified', data: res.data.data }
      }
      return { success: false, message: res.data.message ?? 'Failed to edit update' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to edit update' }
    }
  },

  async deleteTicketUpdate(
    ticketId: string,
    updateId: number
  ): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/tickets/${ticketId}/updates/${updateId}`)
      return { success: true, message: 'Update removed' }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      return { success: false, message: ax.response?.data?.message ?? ax.message ?? 'Failed to remove update' }
    }
  },
}
